import http from '../../../../../apis/http';
import { API_SERVER } from '../../../../../contants';

export const S3_PREFIX = 'https://cixon-finance-docs.s3.amazonaws.com/';

/**
 * Convert a Blob to a base64 data URL with correct MIME type detection.
 * Production S3 proxy may return application/octet-stream; react-pdf needs image/jpeg|png.
 */
export async function blobToDataUrl(blob: Blob, url?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    let mimeType = blob.type;

    if (!mimeType || mimeType === 'application/octet-stream') {
      if (url) {
        const lowerUrl = url.toLowerCase();
        if (lowerUrl.includes('.png') || lowerUrl.includes('png')) mimeType = 'image/png';
        else if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('jpg') || lowerUrl.includes('jpeg')) {
          mimeType = 'image/jpeg';
        } else if (lowerUrl.includes('.gif') || lowerUrl.includes('gif')) mimeType = 'image/gif';
        else if (lowerUrl.includes('.webp') || lowerUrl.includes('webp')) mimeType = 'image/webp';
        else mimeType = 'image/png';
      } else {
        mimeType = 'image/png';
      }
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64Data = result.includes(',') ? result.split(',')[1] : result;

      let detectedMime = mimeType;
      if (base64Data) {
        if (base64Data.startsWith('/9j/4AAQ') || base64Data.startsWith('/9j/')) detectedMime = 'image/jpeg';
        else if (base64Data.startsWith('iVBORw0KGgo')) detectedMime = 'image/png';
        else if (base64Data.startsWith('R0lGODlh')) detectedMime = 'image/gif';
        else if (base64Data.startsWith('UklGR')) detectedMime = 'image/webp';
      }

      if (result.startsWith('data:application/octet-stream') || (result.startsWith('data:') && !result.startsWith(`data:${detectedMime}`))) {
        resolve(`data:${detectedMime};base64,${base64Data}`);
      } else {
        resolve(result);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Bundled docs-app assets (PVM logos / Georgia fonts) — not served by the API server. */
function isBundledFrontendAsset(url: string): boolean {
  return (
    url.startsWith('/images/gps-logo') ||
    url.startsWith('/images/pvm-partners-logo') ||
    url.startsWith('/images/pr-investment-logo') ||
    url.startsWith('/images/squad/squad-fonds-logo') ||
    url.startsWith('/images/squad/advisors/') ||
    url.startsWith('/fonts/georgia')
  );
}

async function fetchStaticAssetBlob(url: string): Promise<Blob> {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const absoluteUrl = origin ? `${origin}${url}` : url;
  const response = await fetch(absoluteUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch static asset (${response.status}): ${url}`);
  }
  return response.blob();
}

function resolveS3Key(url: string): string | null {
  const match = url.match(/cixon-finance-docs\.s3\.amazonaws\.com\/([^?\s#]+)/);
  if (match?.[1]) return match[1];
  if (url.startsWith(S3_PREFIX)) {
    return url.replace(S3_PREFIX, '').split('?')[0].split('#')[0] || null;
  }
  return null;
}

/** Same fetch path as ESG icons / logo in PdfPreviewDialog. */
export async function fetchImageForPdf(imageUrl: string): Promise<string | undefined> {
  const url = imageUrl?.trim();
  if (!url) return undefined;
  if (url.startsWith('data:')) return url;

  try {
    let blob: Blob;
    if (url.startsWith(S3_PREFIX) || url.includes('cixon-finance-docs.s3.amazonaws.com')) {
      const s3Key = resolveS3Key(url);
      if (!s3Key) return undefined;
      blob = await http.getBlob(`/docs/files/download/${s3Key}`);
    } else if (url.startsWith(API_SERVER)) {
      let relativePath = url.replace(API_SERVER, '').replace(/^\/\.\//, '/');
      if (relativePath.startsWith('./')) {
        relativePath = '/' + relativePath.slice(2);
      }
      blob = await http.getBlob(relativePath);
    } else if (url.startsWith('/')) {
      blob = isBundledFrontendAsset(url)
        ? await fetchStaticAssetBlob(url)
        : await http.getBlob(url);
    } else {
      return undefined;
    }
    return await blobToDataUrl(blob, url);
  } catch (e) {
    console.error(`Failed to load image for PDF: ${url}`, e);
    return undefined;
  }
}
